import React, { FC, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from 'redux/store';
import { useNavigate } from 'react-router';
import Footer from 'components/Footer/Index';
import JobCard from 'components/JobCard/Index';
import Navbar from 'components/Navbar';
import useLocales from 'hooks/useLocales';
import { JobService } from 'services/jobs/job';
import { RequestsService } from 'services/requests/request';
import { IApplicationResult } from 'core/interface/jobDTO';
import { JobCardActionEnum } from 'core/enum/jobCardAction';
import CandidateIncomingRequests from 'components/shared/CandidateIncomingRequests';
import Image from 'components/image/Image';
import Pagination from '@material-ui/core/Pagination';

import './Index.scss';
import Popup from 'components/shared/Popups/Popup/Index';
import ProfilePopUp from 'components/shared/ProfilePopUp/Index';

interface RequestsState {
  request: {
    results: IApplicationResult[];
  } | null;
  singleRequest: any;
}

const Applications: FC = (): JSX.Element => {
  const { t } = useLocales();
  const navigate = useNavigate();
  const { applications } = useSelector(
    (state: RootState) => state.applications
  );
  const { request: incomingRequests } = useSelector(
    (state: RootState) => state.requests as RequestsState
  );

  const [openModal, setOpenModal] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState<any>(null);
  // Pagination state for applications
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(6);
  const [offset, setOffset] = useState(0);
  // Pagination state for incoming requests
  const [incomingPage, setIncomingPage] = useState(1);
  const [incomingLimit, setIncomingLimit] = useState(6);
  const [incomingOffset, setIncomingOffset] = useState(0);

  useEffect(() => {
    setOffset((page - 1) * limit);
  }, [page, limit]);
  useEffect(() => {
    setIncomingOffset((incomingPage - 1) * incomingLimit);
  }, [incomingPage, incomingLimit]);

  useEffect(() => {
    getApplications();
  }, [limit, offset]);
  useEffect(() => {
    getCandidateRequests();
  }, [incomingLimit, incomingOffset]);

  // Reset page to 1 when applications change (e.g., after a filter/search in the future)
  useEffect(() => {
    setPage(1);
  }, [applications]);
  // Reset incomingPage to 1 when incomingRequests change
  useEffect(() => {
    setIncomingPage(1);
  }, [incomingRequests]);

  const hasApplications =
    applications?.results && applications.results.length > 0;

  const hasRequest =
    incomingRequests?.results && incomingRequests.results.length > 0;

  const getApplications = (): void => {
    JobService.getApplications(undefined, undefined, limit, offset);
  };

  const getCandidateRequests = (): void => {
    RequestsService.getRequest(
      undefined,
      undefined,
      incomingLimit,
      incomingOffset
    );
  };

  const handleActionComplete = async (): Promise<void> => {
    getApplications();
    getCandidateRequests();
  };

  const handleAccept = async (id: number): Promise<void> => {
    await RequestsService.acceptProfileRequest(id);
    getCandidateRequests();
  };

  const handleDecline = async (id: number): Promise<void> => {
    await RequestsService.denyProfileRequest(id);
    getCandidateRequests();
  };

  const handleClosePopup = () => {
    setOpenModal(false);
    setSelectedRequest(null);
  };

  const handleModal = (id: number, type?: string) => {

    let element;

    if (!type || type === 'incoming-requests' || type === 'teaser-view') {
      element = incomingRequests?.results?.find((el: any) => el.id === id);
    }

    if (!element) {
      return;
    }

    setOpenModal(true);
    setSelectedRequest(element);
  };

  return (
    <div>
      <Navbar />
      <div className="apps fjobs">
        <div className="back_buttons" onClick={() => navigate('/dashboard')}>
          <Image name="arrow-left.svg" />
          {t('Back to profile')}
        </div>
        <div className="middle">
          <div className="apps__matches">
            <div className="app__container">
              <span className="apps__matches-title">{t('Applications')}</span>
              {hasApplications ? (
                <>
                  {applications.results.map(
                    (item: IApplicationResult, index: number) => (
                      <div key={index} className="apps__matches__card">
                        <JobCard
                          id={item.id}
                          jobId={item.job.id}
                          title={item.job.title}
                          sub_title={item.job.description}
                          date={item.created_at}
                          responded_at={item.responded_at}
                          icon={
                            item.job.company.logo ??
                            '/static/images/recrout-logo-letter.jpg'
                          }
                          actions={[
                            JobCardActionEnum.PROFILE_VIEW,
                            JobCardActionEnum.MORE_INFO,
                            JobCardActionEnum.WITHDRAW
                          ]}
                          status={item.status}
                          matches={[
                            { percent: +item.score, title: 'Talent Match' },
                            {
                              percent: +item.culture_score,
                              title: 'Cultural Fit'
                            }
                          ]}
                          onActionComplete={handleActionComplete}
                          style={{ justifyContent: 'space-between' }}
                        />
                      </div>
                    )
                  )}
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      marginTop: 16
                    }}
                  >
                    <Pagination
                      className="pagination"
                      count={Math.ceil(
                        (typeof (applications as any)?.count === 'number'
                          ? (applications as any).count
                          : applications?.results?.length || 0) / limit
                      )}
                      page={page}
                      onChange={(_, value) => setPage(value)}
                      variant="outlined"
                      shape="rounded"
                      showFirstButton
                      showLastButton
                    />
                  </div>
                </>
              ) : (
                <p className="apps__filter__incoming-desc">
                  {t('There are no applications.')}
                  &nbsp;
                  <a className="job-opening" href="/filter-job">
                    {t('job openings tab')}
                  </a>
                </p>
              )}
            </div>

            <div className="app__container">
              <span className="apps__matches-title incoming">
                {t('Incoming requests')}
              </span>
              {hasRequest ? (
                <>
                  {incomingRequests.results.map((item: any, index: number) => (
                    <div key={index} className="apps__matches__card">
                      <CandidateIncomingRequests
                        id={item.id}
                        name={
                          item?.origin_job?.title || item?.origin_profile?.title
                        }
                        sub_title={item?.origin_job?.description || ''}
                        email={item?.origin_job?.company?.email || ''}
                        icon={
                          item?.origin_job?.company?.logo ??
                          '/static/images/recrout-logo-letter.jpg'
                        }
                        score={item.score}
                        culture_score={item.culture_score}
                        date={item.created_at}
                        responded_at={item.responded_at}
                        status={item.status}
                        handleOpenModal={handleModal}
                        candidateId={item?.candidate?.id}
                        handleAccept={handleAccept}
                        handleDecline={handleDecline}
                      />
                    </div>
                  ))}
                  <div
                    style={{
                      display: 'flex',
                      justifyContent: 'center',
                      marginTop: 16
                    }}
                  >
                    <Pagination
                      className="pagination"
                      count={Math.ceil(
                        (typeof (incomingRequests as any)?.count === 'number'
                          ? (incomingRequests as any).count
                          : incomingRequests?.results?.length || 0) /
                          incomingLimit
                      )}
                      page={incomingPage}
                      onChange={(_, value) => setIncomingPage(value)}
                      variant="outlined"
                      shape="rounded"
                      showFirstButton
                      showLastButton
                    />
                  </div>
                </>
              ) : (
                <p className="apps__filter__incoming-desc">
                  {t('There are no incoming requests.')}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="footers">
        <Footer />
      </div>
      {openModal && selectedRequest && (
        <Popup
          className="popUp"
          title={t('Match Profile')}
          description={selectedRequest?.job?.title}
          onClose={handleClosePopup}
        >
          <ProfilePopUp
            selectedRequest={selectedRequest}
            getJob={
              selectedRequest?.job ||
              selectedRequest?.origin_job ||
              selectedRequest?.origin_profile
            }
            id={selectedRequest?.id}
            onClick={() => handleDecline(selectedRequest?.id)}
            status={selectedRequest.status}
            isCandidate
          />
        </Popup>
      )}
    </div>
  );
};

export default Applications;
